<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { mergeWidgetAppearance } from 'shared/helpers/widgetAppearance';
import { INTERCOM_CARD_CLASS } from 'widget/components/intercom/intercomCardClasses';
import { INTERCOM_HOME_HEADER_FADE } from 'widget/components/intercom/intercomHomeTokens';
import IconHome from 'widget/components/intercom/icons/Home.vue';
import IconMessages from 'widget/components/intercom/icons/Messages.vue';
import IconHelp from 'widget/components/intercom/icons/Help.vue';
import IconNews from 'widget/components/intercom/icons/News.vue';
import IconClose from 'widget/components/intercom/icons/Close.vue';

const props = defineProps({
  welcomeHeading: { type: String, default: '' },
  welcomeTagline: { type: String, default: '' },
  websiteName: { type: String, required: true },
  logo: { type: String, default: '' },
  color: { type: String, default: '#1f93ff' },
  appearance: { type: Object, default: () => ({}) },
});

const { t } = useI18n();

const config = computed(() => mergeWidgetAppearance(props.appearance));

const headerColor = computed(() => config.value.header_color || props.color);

const headerBackgroundStyle = computed(() => {
  if (config.value.header_style === 'solid') {
    return { backgroundColor: headerColor.value };
  }
  const end = config.value.header_gradient_end || headerColor.value;
  return {
    background: `linear-gradient(135deg, ${headerColor.value} 0%, ${end} 100%)`,
  };
});

const greetingLine = computed(
  () =>
    props.welcomeTagline ||
    t('INBOX_MGMT.WIDGET_BUILDER.APPEARANCE.PREVIEW_GREETING')
);
const helpLine = computed(
  () =>
    props.welcomeHeading ||
    t('INBOX_MGMT.WIDGET_BUILDER.APPEARANCE.PREVIEW_HELP')
);

const visibleTabs = computed(() =>
  [
    config.value.show_tab_home && {
      label: t('INBOX_MGMT.WIDGET_BUILDER.APPEARANCE.TABS.HOME'),
      icon: IconHome,
      active: true,
    },
    config.value.show_tab_messages && {
      label: t('INBOX_MGMT.WIDGET_BUILDER.APPEARANCE.TABS.MESSAGES'),
      icon: IconMessages,
      badge: 1,
    },
    config.value.show_tab_help && {
      label: t('INBOX_MGMT.WIDGET_BUILDER.APPEARANCE.TABS.HELP'),
      icon: IconHelp,
    },
    config.value.show_tab_news && {
      label: t('INBOX_MGMT.WIDGET_BUILDER.APPEARANCE.TABS.NEWS'),
      icon: IconNews,
    },
  ].filter(Boolean)
);
</script>

<template>
  <div
    class="flex h-full w-80 flex-col overflow-hidden rounded-3xl bg-white shadow-[0_5px_40px_rgba(9,14,21,0.16)]"
  >
    <div class="flex flex-1 flex-col overflow-hidden px-5">
      <div class="relative shrink-0">
        <div class="relative pointer-events-none">
          <div
            class="pointer-events-none absolute left-0 top-0 z-0 h-[290px] w-full"
            aria-hidden="true"
          >
            <div class="absolute inset-0" :style="headerBackgroundStyle" />
            <div
              v-if="config.header_fade !== false"
              class="absolute bottom-0 left-0 h-20 w-full"
              :style="INTERCOM_HOME_HEADER_FADE"
              aria-hidden="true"
            />
          </div>
          <div class="relative z-[1] pb-20 pt-8 pointer-events-auto">
            <div class="mb-[70px] flex h-10 items-center justify-between">
              <img
                v-if="config.show_logo && logo"
                :src="logo"
                :alt="websiteName"
                class="max-h-9 max-w-[140px] shrink-0 object-contain object-left"
              />
              <span
                v-else-if="config.show_logo"
                class="flex size-9 items-center justify-center rounded-lg bg-white/15 text-sm font-semibold text-white"
              >
                {{ websiteName?.charAt(0) }}
              </span>
              <div v-if="config.show_agent_avatars" class="flex">
                <span
                  v-for="index in 3"
                  :key="index"
                  class="size-10 rounded-full border-2 border-[#ededed] bg-white/25"
                  :class="index > 0 ? '-ml-[17px]' : ''"
                />
              </div>
            </div>
            <div
              class="text-[28px] font-semibold leading-[34px] [overflow-wrap:break-word]"
            >
              <h1
                class="m-0 p-0 text-[28px] font-semibold leading-[34px] text-[#fafafa] opacity-60 [text-shadow:0_0_30px_rgba(255,255,255,0.3)]"
              >
                {{ greetingLine }}
              </h1>
              <h1
                class="m-0 p-0 text-[28px] font-semibold leading-[34px] text-[#fafafa] [text-shadow:0_0_30px_rgba(255,255,255,0.3)]"
              >
                {{ helpLine }}
              </h1>
            </div>
          </div>
        </div>
        <span
          class="absolute right-0 top-8 z-[3] flex rounded-[10px] p-2.5 text-[#fafafa]"
        >
          <IconClose />
        </span>
      </div>

      <div
        class="relative z-[1] -mt-[55px] flex flex-1 flex-col gap-[10px] overflow-hidden pb-2"
      >
        <div
          v-if="config.show_ask_card"
          class="flex items-center px-5 py-4"
          :class="[INTERCOM_CARD_CLASS]"
        >
          <div class="min-w-0 flex-1 text-sm leading-[21px]">
            <p class="m-0 text-sm font-semibold leading-5 text-[#14161a]">
              {{ $t('INBOX_MGMT.WIDGET_BUILDER.APPEARANCE.PREVIEW_ASK_TITLE') }}
            </p>
            <p class="text-sm leading-normal text-[#6c6f74]">
              {{
                $t('INBOX_MGMT.WIDGET_BUILDER.APPEARANCE.PREVIEW_ASK_SUBTITLE')
              }}
            </p>
          </div>
          <div class="ml-2 flex shrink-0">
            <span
              class="size-6 rounded-full border-2 border-[#ededed] bg-n-slate-8"
            />
            <span
              class="-ml-2.5 size-8 rounded-[16.7%] border-2 border-[#ededed] bg-n-slate-10"
            />
            <span
              class="-ml-2.5 size-6 rounded-full border-2 border-[#ededed] bg-n-slate-8"
            />
          </div>
        </div>
        <div v-if="config.show_articles" :class="INTERCOM_CARD_CLASS">
          <div
            class="flex h-24 items-end bg-gradient-to-br from-[#313130] to-[#1a1a18] p-3"
          >
            <span class="text-xs font-semibold text-white">
              {{
                $t('INBOX_MGMT.WIDGET_BUILDER.APPEARANCE.PREVIEW_ARTICLE_TITLE')
              }}
            </span>
          </div>
          <div class="px-5 py-4">
            <p class="m-0 text-sm leading-normal text-[#6c6f74]">
              {{
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.APPEARANCE.PREVIEW_ARTICLE_DESCRIPTION'
                )
              }}
            </p>
          </div>
        </div>
        <div v-if="config.show_tab_help" :class="INTERCOM_CARD_CLASS">
          <div class="flex flex-col gap-2 p-2">
            <div
              class="flex h-10 items-center justify-between rounded-lg bg-[#f5f5f5] px-3 text-sm font-semibold text-[#14161a]"
            >
              <span>
                {{
                  $t('INBOX_MGMT.WIDGET_BUILDER.APPEARANCE.PREVIEW_SEARCH_HELP')
                }}
              </span>
              <span class="i-lucide-search size-4" />
            </div>
            <p class="px-3 py-2 text-sm leading-normal text-[#6c6f74]">
              {{
                $t('INBOX_MGMT.WIDGET_BUILDER.APPEARANCE.PREVIEW_HELP_BROWSE')
              }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <nav
      v-if="visibleTabs.length"
      class="flex h-20 shrink-0 justify-between border-t border-[#ededed] bg-white p-0 shadow-[0_0_25px_0_rgba(9,14,21,0.06)]"
    >
      <div
        v-for="tab in visibleTabs"
        :key="tab.label"
        class="relative flex flex-1 flex-col items-center px-[3px] py-[18px]"
      >
        <div class="relative mb-2 flex items-center justify-center">
          <component
            :is="tab.icon"
            :filled="!!tab.active"
            :class="tab.active ? 'text-black' : 'text-[#6c6f74]'"
          />
          <span
            v-if="tab.badge"
            class="absolute right-[calc(100%-17px)] top-[-7px] box-border flex h-4 min-w-4 translate-x-full items-center justify-center rounded-[9px] bg-[#df2020] px-1 text-[11px] font-bold leading-4 text-[#fafafa]"
          >
            {{ tab.badge }}
          </span>
        </div>
        <span
          class="text-center text-sm leading-5"
          :class="
            tab.active
              ? 'font-semibold text-black'
              : 'font-normal text-[#6c6f74]'
          "
        >
          {{ tab.label }}
        </span>
      </div>
    </nav>
  </div>
</template>
